import { type Row } from '@tanstack/react-table'
import { ChevronDown, ChevronUp, Download, FileSpreadsheet, TrendingUp } from 'lucide-react'
import React, { useState } from 'react'
import { CSVLink } from 'react-csv'
import { type Data } from 'react-csv/lib/core'

import { Button, DropdownMenu, Modal } from 'slab'
import { usePostEmployersReportingEmployeeStatus } from 'utils/web_api'
import type {
  PostEmployersReportingEmployeeStatusEmploymentSubtypesItem,
  PostEmployersReportingEmployeeStatusEnrollmentStatusesItem
} from 'utils/web_api.schemas'

import { type EmployeeTableRow } from '../types'
import { getLocaleDateString } from '../utils'

interface EmployeeReportsDropdownProps {
  selectedRowsIds: string[]
  filteredRows: Row<EmployeeTableRow>[]
  selectedPlanYear: number
  filters: Record<string, boolean>
  employeeSubtypesToInclude: string[]
  onDownloadClick?: () => void
  showEnrollmentsReport?: boolean
}

const baseCsvHeaders = [
  { label: 'Employee ID', key: 'id' },
  { label: 'First Name', key: 'first_name' },
  { label: 'Last Name', key: 'last_name' },
  { label: 'Email', key: 'email' },
  { label: 'Start Date', key: 'start_date' },
  { label: 'Employment Type', key: 'employment_subtype' },
  { label: 'Enrollment Status', key: 'enrollment_status' },
]

export function EmployeeReportsDropdown ({
  selectedRowsIds,
  filteredRows,
  selectedPlanYear,
  filters,
  employeeSubtypesToInclude,
  onDownloadClick,
  showEnrollmentsReport = false
}: EmployeeReportsDropdownProps) {
  const [showEnrollmentsModal, setShowEnrollmentsModal] = useState(false)
  const [isGeneratingReport, setIsGeneratingReport] = useState(false)

  const createReportMutation = usePostEmployersReportingEmployeeStatus()

  // Calculate data for client-side CSV
  const fetchDataForCSV = (): Data => {
    const selectedRows = filteredRows
      .filter(row => selectedRowsIds.includes(row.id))
      .map(row => row.original)

    return selectedRowsIds.length > 0 ? selectedRows : filteredRows.map(row => row.original)
  }

  const handleGenerateEnrollmentsReport = async () => {
    setIsGeneratingReport(true)

    try {
      await createReportMutation.mutateAsync({
        data: {
          plan_year: selectedPlanYear,
          employee_ids: selectedRowsIds.length > 0 ? selectedRowsIds : undefined,
          // Only set filter parameters when not using selected employees
          ...(selectedRowsIds.length === 0
            ? {
                enrollment_statuses: Object.entries(filters)
                  .filter(([_, value]) => value)
                  .map(([key, _]) => key as PostEmployersReportingEmployeeStatusEnrollmentStatusesItem),
                employment_subtypes: employeeSubtypesToInclude as PostEmployersReportingEmployeeStatusEmploymentSubtypesItem[],
              }
            : {}),
        }
      })

      setShowEnrollmentsModal(false)
    } catch (error) {
      console.error('Failed to create report:', error)
    } finally {
      setIsGeneratingReport(false)
    }
  }

  const isDisabled = selectedRowsIds.length === 0 && filteredRows.length === 0

  const csvLinkRef = React.useRef<any>(null)

  const handleEmployeeRosterClick = () => {
    if (csvLinkRef.current) {
      csvLinkRef.current.link.click()
    }
    if (onDownloadClick) {
      onDownloadClick()
    }
    // Return false to prevent menu from closing automatically
    return false
  }

  return (
    <>
      <DropdownMenu
        trigger={({ open }) => (
          <Button
            variant='tertiary'
            disabled={isDisabled}
            rightIcon={open ? ChevronUp : ChevronDown}
            leftIcon={Download}
          >
            Reports
          </Button>
        )}
        align='end'
      >
        <DropdownMenu.Item
          leftIcon={FileSpreadsheet}
          onSelect={handleEmployeeRosterClick}
        >
          Employee roster
        </DropdownMenu.Item>

        {showEnrollmentsReport && (
          <DropdownMenu.Item
            leftIcon={TrendingUp}
            onSelect={() => setShowEnrollmentsModal(true)}
          >
            Year-to-date enrollments
          </DropdownMenu.Item>
        )}
      </DropdownMenu>

      <CSVLink
        ref={csvLinkRef}
        data={fetchDataForCSV()}
        headers={baseCsvHeaders}
        filename={`employee-data-${selectedPlanYear}-${getLocaleDateString(
          new Date()
        )}.csv`}
        target='_blank'
        style={{ display: 'none' }}
      />

      <Modal
        open={showEnrollmentsModal}
        onOpenChange={setShowEnrollmentsModal}
        width='medium'
        aria-describedby={undefined}
      >
        <Modal.Header>
          <Modal.Title>Request year-to-date enrollments report</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='space-y-4'>
            <div>
              <p className='mb-3'>
                This report includes:
              </p>
              <ul className='list-disc pl-5 space-y-1 text-sm'>
                <li>Allowances and plan premiums</li>
                <li>Employee plan enrollment details</li>
                <li>Coverage dates and dependent information</li>
              </ul>
            </div>

            <p className='text-sm'>
              This report may take a few minutes to generate.
              You'll receive an email with a download link once it's ready.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className='flex justify-end gap-3 w-full'>
            <Modal.Button
              variant='secondary'
              disabled={isGeneratingReport}
            >
              Cancel
            </Modal.Button>
            <Button
              variant='primary'
              onClick={handleGenerateEnrollmentsReport}
              disabled={isGeneratingReport}
              leftIcon={isGeneratingReport ? undefined : FileSpreadsheet}
            >
              {isGeneratingReport ? 'Generating...' : 'Generate report'}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  )
}
